import { AxiosResponse } from 'axios';
import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import {
    getDefaultHeaders,
    instance as axiosInstance,
    useAlert,
    useAuth,
    withHeaders,
} from '../core';
import { commonShowAlert } from '../lib';
import { GlossaryMetadataItem } from '../pages/glossary-metadata/glossary-metadata-helper';

interface GetGlossaryMetadataByIdProps {
    id: string;
}

interface AddGlossaryMetadataProps {
    glossary: GlossaryMetadataItem;
    token: string;
}

interface DeleteGlossaryMetadataProps {
    id: string;
}

interface UpdateGlossaryMetadataProps {
    updatedGlossary: GlossaryMetadataItem;
    id: string;
}

interface GetGlossaryMetadataByIdProps {
    id: string;
}

const getQueryData = async (
    url: string,
    token: string
): Promise<AxiosResponse<GlossaryMetadataItem[], any>> => {
    const headers = getDefaultHeaders(token);

    return axiosInstance().get(url, withHeaders(headers));
};

const addGlossaryMetadata = (
    props: AddGlossaryMetadataProps
): Promise<AxiosResponse<AddGlossaryMetadataProps, any>> => {
    const { glossary, token } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/glossary`;

    return axiosInstance().post(url, glossary, withHeaders(headers));
};

const deleteGlossaryMetadata = async (
    props: DeleteGlossaryMetadataProps,
    token: string
): Promise<AxiosResponse<void, any>> => {
    const { id } = props;
    const url = `/metadata/glossary/${id}`;
    const headers = getDefaultHeaders(token);

    return axiosInstance().delete(url, withHeaders(headers));
};

const updateGlossaryMetadata = (
    props: UpdateGlossaryMetadataProps,
    token: string
): Promise<AxiosResponse<UpdateGlossaryMetadataProps, any>> => {
    const { updatedGlossary, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/glossary/${id}`;

    return axiosInstance().put(url, updatedGlossary, withHeaders(headers));
};

export const useAddGlossaryMetadata = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    AddGlossaryMetadataProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(addGlossaryMetadata, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Glossary created.'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Glossary Not Saved';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useDeleteGlossaryMetadata = (): UseMutationResult<
    AxiosResponse<void, any>,
    Error,
    DeleteGlossaryMetadataProps,
    unknown
> => {
    const { getToken } = useAuth();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(
        (props: DeleteGlossaryMetadataProps) =>
            deleteGlossaryMetadata(props, getToken()),
        {
            onSuccess: () => {
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Glossary Deleted.'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Glossary Deletion Failed';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const useUpdateGlossaryMetadata = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    UpdateGlossaryMetadataProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { getToken } = useAuth();

    return useMutation(
        (props: UpdateGlossaryMetadataProps) =>
            updateGlossaryMetadata(props, getToken()),
        {
            onSuccess: () => {
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Glossary Updated.'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Glossary Update Failed';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const useGlossaryMetadataById = (
    props: GetGlossaryMetadataByIdProps
): UseQueryResult<AxiosResponse<GlossaryMetadataItem, any>, unknown> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { getToken } = useAuth();
    const { id } = props;

    return useQuery(
        ['glossary-metadata-details', id],
        async () => getQueryData(`/metadata/glossary/${id}`, getToken()),
        {
            retry: false,
            refetchOnWindowFocus: false,
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ??
                    'Unable to retrieve glossary metadata details';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};
